import React, { useEffect, useState } from "react";
import AddFieldModal from "../components/AddFieldModal";
import FormPreviewModal from "../components/FormPreviewModal";
import FeedbackModal from "../components/FeedbackModal";
import { useNavigate } from "react-router-dom";
import { apiRequest } from "../api";
import EditFieldModal from "../components/EditFieldModal";

const CreateFormPage = ({ initialFormData, onSave, onCancel }) => {

    const [formName, setFormName] = useState("");
    const [formType, setFormType] = useState("undergraduate");
    const [fields, setFields] = useState([]);
    const [field, setField] = useState([]);
    const [showModal, setShowModal] = useState(false);
    const [editModal, setEditModal] = useState(false);
    const [showPreview, setShowPreview] = useState(false);
    const [editingFieldIndex, setEditingFieldIndex] = useState(null);
    const [editingFieldData, setEditingFieldData] = useState(null);

    const [isEditing, setIsEditing] = useState(false);
    const [feedbackMessage, setFeedbackMessage] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const navigate = useNavigate();
    const universityId = localStorage.getItem("universityId");
    const token = localStorage.getItem("token");

    // Load initial data for editing
    useEffect(() => {
        if (initialFormData) {
            setIsEditing(true);
            setFormName(initialFormData.name);
            setFormType(initialFormData.type);
            setFields(
                Object.keys(initialFormData.fields).map((key) => ({
                    name: key,
                    ...initialFormData.fields[key],
                }))
            );
        }
    }, [initialFormData]);

    // Add a new field
    const addField = (newFields) => {
        setFields([...fields, ...newFields]);
        setShowModal(false);
    };

    // Delete a field
    const deleteField = (index) => {
        setFields(fields.filter((_, i) => i !== index));
    };

    // Move a field up
    const moveFieldUp = (index) => {
        if (index === 0) return;
        const updatedFields = [...fields];
        [updatedFields[index], updatedFields[index - 1]] = [
            updatedFields[index - 1],
            updatedFields[index],
        ];
        setFields(updatedFields);
    };

    // Move a field down
    const moveFieldDown = (index) => {
        if (index === fields.length - 1) return;
        const updatedFields = [...fields];
        [updatedFields[index], updatedFields[index + 1]] = [
            updatedFields[index + 1],
            updatedFields[index],
        ];
        setFields(updatedFields);
    };

    const handleLogout = (event) => {
        event.preventDefault();
        console.log("Navigating to /login...");
        localStorage.clear();
        navigate("/login");
    };


    /** Edit an existing field */
    const editField = (index) => {
        setEditingFieldIndex(index);
        setEditingFieldData(fields[index]); // Pass the exact field data for editing
        setEditModal(true);
    };

    const saveEditedField = (updatedField) => {
        const newFields = [...fields];
        newFields[editingFieldIndex] = updatedField[0];
        setFields(newFields);
        setEditModal(false);
    };
    // Submit the form
    const submitForm = async (e) => {
        e.preventDefault();

        const formPayload = {
            name: formName,
            type: formType,
            fields: fields.reduce((acc, field) => {
                acc[field.name] = {
                    type: field.type,
                    required: field.required,
                    ...(field.options ? { options: field.options } : {}),
                    ...(field.text ? { text: field.text } : {}),
                };
                return acc;
            }, {}),
            is_default: false,
            university_id: universityId,
        };

        console.log("Submitting Form:", formPayload);
        console.log("Fields before submission:", fields);
        console.log("Fields detailed:", fields.map(f => ({ name: f.name, type: f.type, text: f.text, required: f.required })));
        setIsLoading(true);
        setFeedbackMessage(null);

        try {
            const url = isEditing
                ? `${process.env.REACT_APP_API_BASE_URL}/forms/${initialFormData.id}`
                : `${process.env.REACT_APP_API_BASE_URL}/forms/${universityId}`;
            const method = isEditing ? "PUT" : "POST";

            await apiRequest(url, method, formPayload, token);

            setFeedbackMessage("Form saved successfully!");
            setFields([]);
            setFormName("");
            setFormType("undergraduate");
            setIsEditing(false);

            if (onSave) onSave();
        } catch (error) {
            console.error("Error saving form:", error);
            setFeedbackMessage("Failed to save form. Please try again.");
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <main className="main-content">
            <header className="header component">
                    <span className="component-header">
                        {isEditing ? "Edit Form" : "New Form"}
                    </span>
            </header>
            <hr/>

            <form onSubmit={submitForm}>
                <div className="form-group">
                    <label>Form Name:</label>
                    <input
                        type="text"
                        className="form-control"
                        value={formName}
                        required
                        onChange={(e) => setFormName(e.target.value)}
                        disabled={isLoading}
                    />
                </div>
                <div className="form-group">
                    <label>Form Type:</label>
                    <select
                        className="form-control"
                        value={formType}
                        onChange={(e) => setFormType(e.target.value)}
                        disabled={isLoading}
                    >
                        <option value="undergraduate">Undergraduate</option>
                        <option value="graduate">Graduate</option>
                        <option value="hybrid">Hybrid</option>
                    </select>
                </div>
                <div className="divider-text gap-top-45 gap-bottom-45">
                    <span>Form Fields</span>
                </div>

                <button
                    type="button"
                    className="btn btn-info btn-add-field"
                    onClick={() => setShowModal(true)}
                    disabled={isLoading}
                >
                    +
                </button>
                <ul>
                    {fields.map((field, index) => (
                        <li className="field" key={index}>
                            {field.name} ({field.type}) {field.required ? "(*Required)" : ""} &nbsp;&nbsp;
                            <button
                                className="btn btn-warning btn-edit-field"
                                type="button"
                                onClick={() => editField(index)}
                                disabled={isLoading}
                            >
                                Edit
                            </button>&nbsp;
                            <button
                                className="btn btn-primary clone-btn-right"
                                type="button"
                                onClick={() => moveFieldUp(index)}
                                disabled={isLoading}
                            >
                                ↑
                            </button>
                            &nbsp;
                            <button
                                className="btn btn-primary clone-btn-right"
                                type="button"
                                onClick={() => moveFieldDown(index)}
                                disabled={isLoading}
                            >
                                ↓
                            </button>
                            &nbsp;
                            <button
                                className="btn btn-default clone-btn-right delete"
                                type="button"
                                onClick={() => deleteField(index)}
                                disabled={isLoading}
                            >
                                -
                            </button>
                        </li>
                    ))}
                </ul>
                <div className="mt-3">
                    <button
                        type="submit"
                        className="btn btn-primary mr-2"
                        disabled={isLoading}
                    >
                        {isEditing ? "Update Form" : "Save Form"}
                    </button>
                    <button
                        type="button"
                        className="btn btn-secondary"
                        onClick={() => setShowPreview(true)}
                        disabled={isLoading}
                    >
                        Preview Form
                    </button>&nbsp;&nbsp;
                    <button
                        type="button"
                        className="btn btn-outline-secondary btn-reset-form"
                        onClick={() => {
                            setFields([]);
                            setFormName("");
                            setFormType("undergraduate");
                            setIsEditing(false);
                            if (onCancel) onCancel();
                        }}
                        disabled={isLoading}
                    >
                        Reset Form
                    </button>&nbsp;&nbsp;
                    <a
                        type="button"
                        className="btn btn-secondary btn-cancel-form"
                        href="/forms">
                        Cancel
                    </a>
                </div>
            </form>
            {showModal && (
                <AddFieldModal
                    onSave={addField}
                    onClose={() => setShowModal(false)}
                />
            )}
            {editModal && (
                <EditFieldModal
                    field={editingFieldData}
                    fieldIndex={editingFieldIndex}
                    onSave={saveEditedField}
                    onClose={() => setEditModal(false)}
                />
            )}
            {showPreview && (
                <FormPreviewModal
                    fields={fields}
                    onClose={() => setShowPreview(false)}
                />
            )}
            {feedbackMessage && (
                <FeedbackModal
                    message={feedbackMessage}
                    onClose={() => setFeedbackMessage(null)}
                />
            )}
        </main>
    );
};

export default CreateFormPage;
